import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { UserPasswordChangeService } from './user-password-change.service';
import { AppBarTitleService } from 'common';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SsoAccountData } from 'common';

@UntilDestroy()
@Component({
    selector: 'ifp-user-password-change-form',
    templateUrl: './user-password-change-form.component.html',
    styleUrls: ['./user-password-change-form.component.scss'],
    standalone: false
})
export class UserPasswordChangeFormComponent implements OnInit {

  data: UserPasswordChangeFormComponentData;
  submitSub: Subscription;
  form: UntypedFormGroup;

  constructor(
    private route: ActivatedRoute,
    private userPasswordChangeService: UserPasswordChangeService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    public snackBar: MatSnackBar,
    private appBarTitleService: AppBarTitleService) {
      this.formInit();
  }

  private formInit(): void {
    this.form = this.formBuilder.group({
      currentPassword: ['', Validators.compose([Validators.required])],
      newPassword: ['', Validators.compose([Validators.required, Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/)])],
      confirmPassword: ['', Validators.required]
    }, { validator: formPasswordConfirmationValidator('newPassword', 'confirmPassword') });
  }

  ngOnInit() {
    this.route.data.pipe(untilDestroyed(this))
      .subscribe(this.dataInit.bind(this));

    this.appBarTitleService.title = 'Update your password';
  }

  dataInit(data: UserPasswordChangeFormComponentData) {
    this.data = data;
  }

  submit() {
    if (!this.form.valid) return;
    this.submitSub = this.userPasswordChangeService
      .submit(this.form.value, this.data.currentAccount.id)
      .subscribe(this.submitSuccess, this.submitError);
  }

  submitSubClear() {
    this.submitSub.unsubscribe();
    this.submitSub = null;
  }

  submitError = response => {
    if (response.status === 401)
      this.snackBar.open('The password provided was incorrect. Double check this info and try again.', 'Error',
        { duration: 2000, panelClass: ['mat-red-bg'] });
    else {
      const message = response.error ? response.error.error_description : 'Error ocurred while trying to change password';
      this.snackBar.open(message, 'Error', { duration: 2000, panelClass: ['mat-red-bg'] });
    }
    this.submitSubClear();
  }

  submitSuccess = () => {
    this.snackBar.open("Your password has been changed.", "OK", { duration: 2000, panelClass: ['mat-green-bg'] });
    this.router.navigate(["dashboard"]);
  }
}

function formPasswordConfirmationValidator(controlName: string, matchingControlName: string) {
  return (formGroup: UntypedFormGroup) => {
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];

    if (matchingControl.errors) {
      return;
    }

    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ mustMatch: true });
    } else {
      matchingControl.setErrors(null);
    }
  };
}

export interface UserPasswordChangeFormComponentData {
  currentAccount: SsoAccountData;
}
